/**
 * Thin HTTP client for the promo superadmin surface (/api/admin/billing).
 *
 * These endpoints are platform-admin only and 404 when BILLING_ENABLED is off,
 * so the Promotions panel that consumes this client is rendered only inside the
 * Tier-1 platform view. Types are the TRANSPORT shape (dates as ISO strings,
 * money as billing-currency micro — the same units the promo effects carry,
 * each stamped with its own `billingCurrency`).
 *
 * Server contract: features/billing/api/admin-routes.ts.
 * Plan: docs/plans/2026-07-22_PROMO_ACCESS_MODULAR_BILLING.md (Phase P3).
 */
import { request } from '@papan/api-client'
import type { PromoEffect, PromoEffectInput } from '@papan/types'

export interface PromoGrantView {
  id: string
  tenantId: string
  /** Present only in the unscoped cross-tenant feed. */
  tenantName?: string | null
  effects: PromoEffect[]
  startsAt: string
  endsAt: string
  source: 'admin' | 'code'
  codeId: string | null
  grantedByEmail: string | null
  note: string | null
  revokedAt: string | null
  revokedByEmail: string | null
  /** Campaign tag (C1) — null for untagged one-offs. */
  groupTag?: string | null
  /** Draw-down against this grant's `credit` effects, billing-currency micro
   *  (C1) — in the currency those effects are stamped with, not USD. */
  creditConsumedBillingMicro?: number
  createdAt: string
}

export interface PromoCodeView {
  id: string
  code: string
  label: string | null
  effects: PromoEffect[]
  durationDays: number | null
  grantStartsAt: string | null
  grantEndsAt: string | null
  maxRedemptions: number | null
  redemptionCount: number
  perTenantLimit: number
  expiresAt: string | null
  revokedAt: string | null
  createdByEmail: string | null
  /** Campaign tag (C1) — inherited by every grant this code mints. */
  groupTag?: string | null
  createdAt: string
}

export interface CreateGrantInput {
  tenantId: string
  /** The INPUT shape — no `billingCurrency`. The server stamps the deployment's
   *  own at mint; a client cannot name the currency of a grant. */
  effects: PromoEffectInput[]
  startsAt: string
  endsAt: string
  note?: string
  groupTag?: string
}

export interface CreateCodeInput {
  /** Input shape — see `CreateGrantInput.effects`. */
  effects: PromoEffectInput[]
  /** Optional vanity code; omit for a generated 26-char base32 code. */
  code?: string
  label?: string
  /** Rolling window (XOR the fixed pair below). */
  durationDays?: number
  grantStartsAt?: string
  grantEndsAt?: string
  /** null = unlimited redemptions; 1 = one-time link. */
  maxRedemptions?: number | null
  perTenantLimit?: number
  expiresAt?: string
  groupTag?: string
}

/**
 * What a campaign revoke would take (F-29).
 *
 * Money is BILLING-CURRENCY micro, denominated in `billingCurrency` below —
 * not USD microdollars, which is what this docblock said until the money path
 * was re-denominated. The name of a field is part of its unit, and so is the
 * comment above it.
 */
export interface CampaignPreview {
  groupTag: string
  /** The unit every `*BillingMicro` figure here is in — the deployment's own.
   *  OPTIONAL for a rolling deploy: the SPA and the API ship separately, so a
   *  newer client can meet an older server that does not send it. Absent means
   *  "unknown", never a default — rendering a guessed currency is the defect
   *  this field was added to fix. */
  billingCurrency?: string
  grants: {
    /** Active, un-revoked grants carrying the tag. */
    grants: number
    tenants: number
    /** Granted credit not yet spent — what revoking actually removes. */
    unspentCreditBillingMicro: number
  }
  codes: {
    /** Un-revoked codes carrying the tag. */
    codes: number
    /** Redemptions already made against them. Revoking does NOT undo these —
     *  the grants they minted are the grants half above. */
    redemptions: number
  }
}

export const promoAdminApi = {
  /** Per-tenant history when `tenantId` is given, else a paginated feed. */
  listGrants: (opts: { tenantId?: string; limit?: number; offset?: number } = {}) => {
    const q = new URLSearchParams()
    if (opts.tenantId) q.set('tenantId', opts.tenantId)
    if (opts.limit != null) q.set('limit', String(opts.limit))
    if (opts.offset != null) q.set('offset', String(opts.offset))
    const qs = q.toString()
    return request<{ grants: PromoGrantView[]; total?: number; limit?: number; offset?: number }>(
      `/api/admin/billing/promo-grants${qs ? `?${qs}` : ''}`,
    )
  },

  createGrant: (input: CreateGrantInput) =>
    request<{ grant: PromoGrantView }>('/api/admin/billing/promo-grants', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  revokeGrant: (id: string) =>
    request<{ grant: PromoGrantView }>(`/api/admin/billing/promo-grants/${id}/revoke`, {
      method: 'POST',
    }),

  listCodes: () => request<{ codes: PromoCodeView[] }>('/api/admin/billing/promo-codes'),

  createCode: (input: CreateCodeInput) =>
    request<{ code: PromoCodeView }>('/api/admin/billing/promo-codes', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  revokeCode: (id: string) =>
    request<{ code: PromoCodeView }>(`/api/admin/billing/promo-codes/${id}/revoke`, {
      method: 'POST',
    }),

  /**
   * F-29: what the two campaign revokes below would take, without taking it.
   * Server-side, from the same predicates the revokes use — a client-side
   * count off the paginated grants feed could only ever see one page.
   */
  previewCampaign: (groupTag: string) =>
    request<CampaignPreview>(
      `/api/admin/billing/promo-campaign-preview?groupTag=${encodeURIComponent(groupTag)}`,
    ),

  /** C1: revoke a whole campaign's ACTIVE grants (audited per tenant). */
  revokeGrantsByTag: (groupTag: string) =>
    request<{ revokedGrants: number; affectedTenants: number }>(
      '/api/admin/billing/promo-grants/revoke-by-tag',
      { method: 'POST', body: JSON.stringify({ groupTag }) },
    ),

  /** C1: stop future redemptions of a campaign's codes (grants untouched). */
  revokeCodesByTag: (groupTag: string) =>
    request<{ revokedCodes: number }>(
      '/api/admin/billing/promo-codes/revoke-by-tag',
      { method: 'POST', body: JSON.stringify({ groupTag }) },
    ),
}
